import React from 'react';
import styled from 'styled-components';

import { Command, tabActions } from '@store/slices';
import { Label } from '@common/Label';
import { Alert, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useNodeData } from '@common/hooks/useNodeData';
import { useDispatch, useSelector } from 'react-redux';
import { NodeField } from '@components/Flow/Nodes';
import { State } from '@store/index';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  grid-gap: 8px;
`;

interface Props {
  command: Command;
  field: NodeField;
}

export const ToggleStream: React.FC<Props> = ({ command, field }) => {
  const dispatch = useDispatch();

  const { state } = command;
  const { id, options, label, description } = field;

  const { isStreamDisabled } = useNodeData();

  const commands = useSelector((state: State) => state.tabs.active.commands);

  const value = state[id];
  const streamDisabled = command?.config?.command_id === 'http.response' && isStreamDisabled(command.node.id);

  const triggerCommand = commands.find((command) => command.config.trigger);

  const isModule = triggerCommand?.config?.command_id === 'workflow.module';

  const toggleEnabled = (newVal: string) => {
    dispatch(tabActions.setSaved(false));

    dispatch(
      tabActions.nodeInputUpdate({
        nodeId: command.node.id,
        fieldId: field.id,
        value: newVal,
      })
    );
  };

  if (isModule) {
    return (
      <Container>
        <Alert severity='info'>
          The response type for a module is not determined inside the module. When you use this module as a node in a
          workflow, you can set the response type by defining it in the Response node connected to the module node.
        </Alert>
      </Container>
    );
  }

  return (
    <Container>
      <Label title={label} tooltip={description} />
      <ToggleButtonGroup
        size='small'
        color='primary'
        value={streamDisabled ? 'async' : value}
        exclusive
        onChange={(_, newVal) => newVal !== undefined && newVal !== null && toggleEnabled(newVal)}
        fullWidth
      >
        {options?.map((option, index) => (
          <ToggleButton
            size='small'
            key={index}
            value={option.value}
            disabled={option.label === 'Stream' && streamDisabled}
          >
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Container>
  );
};
